import React, { useState } from "react";
import { Client } from "@twilio/conversations";
import { ChevronDoubleLeftIcon } from "@twilio-paste/icons/esm/ChevronDoubleLeftIcon";
import { Box, Input } from "@twilio-paste/core";
import { ChevronDoubleRightIcon } from "@twilio-paste/icons/esm/ChevronDoubleRightIcon";
import styles from "../../styles";
import { UnorderedListIcon } from "@twilio-paste/icons/esm/UnorderedListIcon";
import { EmailIcon } from "@twilio-paste/icons/esm/EmailIcon";
import { useDispatch, useSelector } from "react-redux";
import {
  loadMoreConversations,
  newFilterConversations,
} from "../../store/action-creators";
import { AppState } from "../../store";
import { getTranslation } from "../../utils/localUtils";
import { SearchIcon } from "@twilio-paste/icons/esm/SearchIcon";
import ShowOn from "../icons/ShowOn";
import ShowOff from "../icons/ShowOff";
import NewCreateConversationButton from "./NewCreateConversationButton";
import { NewConversationsList } from "./NewConversationsList";
import { ConversationDto } from "../../dto/conversation";
import { Button } from "@twilio-paste/button";

interface NewConvosContainerProps {
  client?: Client;
  conversation?: ConversationDto;
}

const NewConversationsContainer: React.FC<NewConvosContainerProps> = (
  props: NewConvosContainerProps
) => {
  const [listHidden, hideList] = useState(false);
  const [showHidden, setShowHidden] = useState(true);
  const [viewMode, setViewMode] = useState<"all" | "unread" | null>(null);

  const dispatch = useDispatch();

  const local = useSelector((state: AppState) => state.local);
  const search = getTranslation(local, "convoSearch");

  const handleSearch = (searchString: string) => {
    dispatch(newFilterConversations(searchString));
  };

  const handleToggleView = () => {
    setViewMode((prev) => (prev === "all" ? null : "all"));
  };
  const handleEmailFilter = () => {
    setViewMode((prev) => (prev === "unread" ? null : "unread"));
  };
  const handleToggleHidden = () => {
    setShowHidden((prev) => !prev);
  };

  const eyeTitle = showHidden ? "Filtro Marketo: ON" : "Filtro Marketo: OFF";

  const handleLoadMore = () => {
    dispatch(loadMoreConversations());
  };

  return (
    <Box
      style={
        listHidden
          ? { ...styles.convosWrapper, ...styles.collapsedList }
          : styles.convosWrapper
      }
    >
      <Box style={styles.newConvoButton}>
        <NewCreateConversationButton
          client={props.client}
          collapsed={listHidden}
        />
        {!listHidden && (
          <Box marginTop="space60">
            <Input
              aria-describedby="convo_string_search"
              id="convoString"
              name="convoString"
              type="text"
              placeholder={search}
              onChange={(e) => handleSearch(e.target.value)}
              required
              autoFocus
              insertBefore={<SearchIcon decorative size="sizeIcon20" />}
              insertAfter={
                <Box
                  as="span"
                  display="inline-flex"
                  alignItems="center"
                  columnGap="space30"
                >
                  <Box
                    as="button"
                    type="button"
                    aria-label="Filtro tutti i messaggi"
                    title="Filtro tutti i messaggi"
                    aria-pressed={viewMode === "all"}
                    onClick={handleToggleView}
                    display="inline-flex"
                    alignItems="center"
                    padding="space20"
                    backgroundColor={
                      viewMode === "all"
                        ? "colorBackgroundPrimaryWeaker"
                        : "colorBackgroundBody"
                    }
                    border="none"
                    cursor="pointer"
                    borderRadius="borderRadius20"
                    style={{ appearance: "none" }}
                    color={
                      viewMode === "all" ? "colorTextLink" : "colorTextWeak"
                    }
                  >
                    <UnorderedListIcon
                      decorative={false}
                      title="Filtro tutti i messaggi"
                      size="sizeIcon20"
                    />
                  </Box>

                  <Box
                    as="button"
                    type="button"
                    aria-label="Filtro messaggi non letti"
                    title="Filtro messaggi non letti"
                    aria-pressed={viewMode === "unread"}
                    onClick={handleEmailFilter}
                    display="inline-flex"
                    alignItems="center"
                    padding="space20"
                    backgroundColor={
                      viewMode === "unread"
                        ? "colorBackgroundPrimaryWeaker"
                        : "colorBackgroundBody"
                    }
                    border="none"
                    cursor="pointer"
                    borderRadius="borderRadius20"
                    style={{ appearance: "none" }}
                    color={
                      viewMode === "unread" ? "colorTextLink" : "colorTextWeak"
                    }
                  >
                    <EmailIcon
                      decorative={false}
                      title="Filtro messaggi non letti"
                      size="sizeIcon20"
                    />
                  </Box>

                  <Box
                    as="button"
                    type="button"
                    aria-label={eyeTitle}
                    title={eyeTitle}
                    aria-pressed={showHidden}
                    onClick={handleToggleHidden}
                    display="inline-flex"
                    alignItems="center"
                    padding="space20"
                    backgroundColor={
                      showHidden
                        ? "colorBackgroundPrimaryWeaker"
                        : "colorBackgroundBody"
                    }
                    border="none"
                    cursor="pointer"
                    borderRadius="borderRadius20"
                    style={{ appearance: "none" }}
                    color={showHidden ? "colorTextLink" : "colorTextWeak"}
                  >
                    {showHidden ? <ShowOn /> : <ShowOff />}
                  </Box>
                </Box>
              }
            />
          </Box>
        )}
      </Box>
      <Box style={styles.convoList}>
        {!listHidden ? (
          <>
            <NewConversationsList
              viewMode={viewMode}
              hideMarketo={showHidden}
            />

            <Box padding="space40">
              <Button variant="secondary" fullWidth onClick={handleLoadMore}>
                Mostra altro
              </Button>
            </Box>
          </>
        ) : null}
      </Box>
      <Box style={styles.collapseButtonBox}>
        <Box
          paddingTop="space30"
          style={{
            paddingLeft: 10,
            paddingRight: 10,
          }}
          onClick={() => hideList(!listHidden)}
        >
          {listHidden ? (
            <ChevronDoubleRightIcon decorative={false} title="Collapse" />
          ) : (
            <ChevronDoubleLeftIcon decorative={false} title="Collapse" />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NewConversationsContainer;
